// Contacts List Page
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchCrmEntities } from '../../lib/queryFunctions';
import { useAuth } from '../../context/AuthContext';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Building2, User, ChevronRight } from 'lucide-react';
import { TableToolbar } from '../../components/common/TableToolbar';

const CONTACTS_PARAMS = { crm_class: 'contact', include_archived: true };

const ContactsListPage = () => {
  const { isViewOnly, isTenantAdmin, isSystemOwner } = useAuth();
  const viewOnly = isViewOnly();
  const isAdmin = isTenantAdmin() || isSystemOwner();
  const navigate = useNavigate();

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: contacts = [], isLoading } = useQuery({
    queryKey: QK.crmEntities(CONTACTS_PARAMS),
    queryFn: fetchCrmEntities(CONTACTS_PARAMS),
    onError: () => toast.error('Failed to load contacts'),
  });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [hideArchived, setHideArchived] = useState(true);

  // ── Derived ───────────────────────────────────────────────────────────────
  const roleOptions = useMemo(() => {
    const roles = new Set();
    contacts.forEach(c => {
      const role = c.organization_industry_role || c.person_industry_role;
      if (role) roles.add(role);
    });
    return Array.from(roles).sort();
  }, [contacts]);

  const getSortKey = (entity) => {
    if (entity.crm_type === 'person') {
      const last = entity.person_last_name || '';
      const first = entity.person_first_name || '';
      return last ? `${last} ${first}`.toLowerCase() : (entity.system_name || '').toLowerCase();
    }
    return (entity.system_name || '').toLowerCase();
  };

  const filteredContacts = useMemo(() => contacts.filter((contact) => {
    const matchesSearch =
      contact.system_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.organization_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.person_full_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      filterType === 'all' ||
      (filterType === 'organization' && contact.crm_type === 'organization') ||
      (filterType === 'person' && contact.crm_type === 'person');

    const role = contact.organization_industry_role || contact.person_industry_role;
    const matchesRole     = filterRole === 'all' || role === filterRole;
    const matchesArchived = !hideArchived || !contact.archived;

    return matchesSearch && matchesType && matchesRole && matchesArchived;
  }).sort((a, b) => getSortKey(a).localeCompare(getSortKey(b))),
  [contacts, searchTerm, filterType, filterRole, hideArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <SidebarLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6" data-testid="contacts-list-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-semibold">Contacts</h1>
            <p className="text-muted-foreground mt-1">All Contacts</p>
          </div>
          <div className="flex flex-col gap-2 items-end">
            {isAdmin && contacts.some(c => c.archived) && (
              <Button
                variant={hideArchived ? 'outline' : 'secondary'}
                size="sm"
                className="h-8 text-xs gap-1.5"
                onClick={() => setHideArchived(h => !h)}
                data-testid="toggle-archived-contacts"
              >
                {hideArchived ? 'Show Archived' : 'Hide Archived'}
              </Button>
            )}
            {!viewOnly && (
              <Button
                onClick={() => navigate('/crm/contacts/new')}
                className="btn-primary w-full sm:w-auto"
                data-testid="new-contact-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> New Contact
              </Button>
            )}
          </div>
        </div>

        <TableToolbar
          tableId="contacts-list"
          data={filteredContacts}
          filename="contacts"
          columns={[
            { key: 'system_name', label: 'Contact' },
            { key: 'person_industry_role', label: 'Industry Role' },
            { key: 'account_rating', label: 'Rating' },
          ]}
        />

        {/* Table */}
        <Card>
          <Table data-table-id="contacts-list">
            <TableHeader>
              <TableRow className="bg-muted/20">
                {/* Contact Column with Search */}
                <TableHead className="min-w-[300px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground uppercase text-sm">Contact</span>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search contacts..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8 h-8 text-xs bg-white"
                        data-testid="search-contacts-input"
                      />
                    </div>
                  </div>
                </TableHead>

                {/* Industry Role Column */}
                <TableHead className="min-w-[180px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground uppercase text-sm">Industry Role</span>
                    <Select value={filterRole} onValueChange={setFilterRole}>
                      <SelectTrigger className="h-8 text-xs bg-white">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {roleOptions.map((role) => (
                          <SelectItem key={role} value={role}>
                            {role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TableHead>

                <TableHead className="w-[50px] bg-muted/20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <User className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                    <p className="text-muted-foreground">No contacts found</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => navigate('/crm/contacts/new')}
                    >
                      <Plus className="w-4 h-4 mr-2" /> Create First Contact
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredContacts.map((contact) => (
                  <TableRow
                    key={contact.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/crm/accounts/${contact.id}`, { state: { entityType: 'contact' } })}
                    data-testid={`contact-row-${contact.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary flex items-center justify-center shrink-0">
                          {contact.main_photo_url ? (
                            <img
                              src={contact.main_photo_url.startsWith('http')
                                ? contact.main_photo_url
                                : `${window.location.origin}${contact.main_photo_url}`}
                              alt={contact.system_name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : contact.crm_type === 'organization' ? (
                            <Building2 className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <User className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{contact.system_name}
                            {contact.archived && <Badge variant="outline" className="ml-2 border-orange-400 text-orange-600 text-[10px]">Archived</Badge>}
                          </p>
                          {contact.crm_type === 'person' && contact.organization_name && (
                            <p className="text-xs text-muted-foreground">{contact.organization_name}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm capitalize">
                        {contact.organization_industry_role?.replace(/_/g, ' ') ||
                          contact.person_industry_role?.replace(/_/g, ' ') ||
                          '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </SidebarLayout>
  );
};

export default ContactsListPage;
